import { apiClient, baseUrl, downloadServerPdf } from './api.js';
import { unwrap } from './api-error.js';
import type { ApiPaths } from '@reos/api-client';

/**
 * Smlouvy v adminu REOS (krok T3): typovany klient nad `/v1/contracts` a
 * `/v1/contract-templates` (viz `packages/contexts/documents/src/public/plugin.ts`).
 * Odpovedi jsou strict zod (na rozdil od FAK3 rent-api), ale DTO typy pisu
 * rucne stejne jako tam - stejny vzor v celem web-adminu, request body
 * chodi z generovaneho OpenAPI kontraktu.
 */

export type ContractType =
  'najem-byt' | 'najem-nebyt' | 'kupni' | 'rezervace' | 'zprostredkovatelska';
export type ServiceState = 'v-zalohach' | 'prevedeno' | 'sami' | 'nedodava-se';
export type SubjectTypeKey = 'byt' | 'dum' | 'garaz' | 'sklad' | 'prodejna';
export type ContractStatusValue =
  'draft' | 'ready_to_send' | 'awaiting_signature' | 'signed' | 'archived';
export type ContractFieldSource = 'broker' | 'client' | 'reos_data';
export type OfficeTemplateStatus = 'draft' | 'active' | 'archived';
export type MappingDecision = 'potvrdit' | 'zmenit' | 'rucne' | 'nechat-text';
export type SuggestionConfidence = 'jiste' | 'pravdepodobne' | 'nejiste';
/** Krok EDIT3: `pozdeji` = pole odsunute na „doplnit později" (nebrani odeslani). */
export type CheckSeverity = 'chyba' | 'varovani' | 'pozdeji';

/** Krok T4: puvod udaje ve smlouve (server `internal/contracts/origin.ts`). */
export type FieldOrigin =
  | 'kat'
  | 'klient'
  // Krok P8F4 bod 3: zpresneni puvodu — udaj dosadila zakazka („Doplnit ze
  // zakázky": nabor na prohlidce / dotaznik majitele), ne magic link.
  | 'majitel-nabor'
  | 'makler'
  | 'makler-nabor'
  | 'pozdeji'
  | 'chybi';

export interface SubjectFormsDto {
  readonly key: string;
  readonly label: string;
  pad: string[];
  readonly rod: 'm' | 'z';
  readonly nebyt: boolean;
}

export interface ContractBlockDto {
  readonly id: string;
  readonly text: string;
  readonly locked?: boolean;
  readonly manualOverride?: string;
  readonly condition?: string;
}

export interface ContractArticleDto {
  readonly id: string;
  readonly title: string;
  /** Mutable (ne readonly): clanky prochazi editorem i importni mapovaci obrazovkou. */
  blocks: ContractBlockDto[];
}

export type MappingSuggestionKind =
  | 'zvyrazneni'
  | 'maska-tecky'
  | 'maska-xxx'
  | 'navesti'
  | 'token-ve-wordu'
  | 'tvar-predmetu'
  | 'tvar-strany'
  | 'podmineny-blok';

export interface MappingSuggestionDto {
  readonly id: string;
  readonly snippet: string;
  readonly kind: MappingSuggestionKind;
  readonly fieldKey: string | null;
  /**
   * Lidsky nazev pole ze slovniku (krok SML6 bod 1). Chodi ze SERVERU, aby
   * mapovaci obrazovka nikdy nemusela kreslit technicky klic
   * (`pronajimatel[].jmeno`) — ani kdyz se katalog poli nestihne nacist.
   */
  readonly fieldLabel?: string | null;
  /** Veta kolem mista: co v dokumentu stoji pred nim a za nim (SML6 bod 1). */
  readonly contextBefore?: string;
  readonly contextAfter?: string;
  readonly confidence: SuggestionConfidence;
  readonly confidencePct: number;
  readonly repeatable: boolean;
  readonly decision?: MappingDecision;
  readonly customField?: string;
}

/**
 * Krok APP3 bod 3 (nalez A34): potvrzene rozhodnuti, ktere import do sablony
 * NEDOSTAL, a duvod. Mapovaci obrazovka ho vypise, nez sablonu ulozi.
 */
export type MappingSkipReason = 'nejednoznacny-usek' | 'usek-nenalezen' | 'bez-pole';

export interface MappingSkipDto {
  readonly suggestionId: string;
  readonly snippet: string;
  readonly fieldKey: string | null;
  readonly reason: MappingSkipReason;
}

export interface InflectionHitDto {
  readonly word: string;
  readonly count: number;
  readonly fieldKey: string;
  readonly padGuess: number;
  readonly certain: boolean;
}

export interface ContractCheckDto {
  readonly code: string;
  readonly severity: CheckSeverity;
  readonly message: string;
  /**
   * Krok EDIT2 bod 3: pole, ktereho se kontrola tyka (`chybi-povinne`).
   * Nepovinne — nasazene API ho posilat nemusi a kontroly bez pole existuji.
   */
  readonly fieldKey?: string | null | undefined;
}

/** Definice vlastniho pole vzoru kancelare (NOC3 bod 6). */
export interface OfficeTemplateFieldDto {
  readonly key: string;
  readonly label: string;
  readonly type: string;
  readonly source: string;
}

export interface OfficeTemplateDto {
  readonly id: string;
  readonly name: string;
  readonly contractType: ContractType;
  readonly basedOnDemoId: string | null;
  readonly version: number;
  readonly status: OfficeTemplateStatus;
  readonly articles: readonly ContractArticleDto[];
  readonly repeatBlocks: readonly string[];
  readonly repeatItemTemplate: Readonly<Record<string, string>>;
  readonly mapping: readonly MappingSuggestionDto[];
  /**
   * Krok NOC3 bod 6 (nalez N14): vlastni pole vzoru vcetne CESKEHO POPISKU
   * (`contract_office_templates.extra_fields`). Nepovinne kvuli starsi
   * odpovedi serveru — editor pak spadne na nazev odvozeny z klice.
   */
  readonly extraFields?: readonly OfficeTemplateFieldDto[] | undefined;
  /**
   * Krok UXD3 bod 2: „Vyplněné údaje tučně" na vzoru kancelare (vychozi
   * zapnuto). Nepovinne kvuli starsi odpovedi serveru.
   */
  readonly boldFilledFields?: boolean;
  readonly supersedesId: string | null;
  readonly createdAt: string;
  readonly updatedAt: string;
}

export interface DemoTemplateSummaryDto {
  readonly id: string;
  readonly nazev: string;
  readonly skupina: 'A' | 'B' | 'C' | 'D';
  readonly zakladniTyp: ContractType;
  readonly requiredCount: number;
  readonly lawyerNotes: readonly string[];
}

export interface ServiceCatalogEntryDto {
  readonly key: string;
  readonly label: string;
  readonly defaultState: ServiceState;
}

/** ADM1 bod 2: lidsky popisek pole (misto technickeho klice v editoru). */
export interface ContractFieldDictionaryEntryDto {
  readonly key: string;
  readonly label: string;
  readonly source: string;
  /** Krok T5: `volba` / `seznam` / `datum` / `cislo` / `castka-kc` / `text` … */
  readonly type?: string;
  /** Nabidka hodnot (mockup: prepinace a zaskrtavatka misto textu). */
  readonly options?: readonly string[];
  readonly allowCustomOption?: boolean;
  readonly repeatable?: boolean;
  /**
   * Krok UXD3b (nalez SML5): typy smluv, pro ktere pole ma smysl — ROZHODL
   * SERVER (`fieldContractTypes`). Chybi = pole plati pro vsechny typy
   * (a starsi server atribut neposila vubec).
   */
  readonly contractTypes?: readonly ContractType[];
}

export interface ContractCatalogsDto {
  readonly subjectTypes: readonly SubjectFormsDto[];
  readonly services: readonly ServiceCatalogEntryDto[];
  readonly fields: readonly ContractFieldDictionaryEntryDto[];
}

export interface CustomSubjectDto {
  readonly id: string;
  readonly key: string;
  readonly forms: SubjectFormsDto;
  readonly createdAt: string;
}

export interface ContractFieldValueDto {
  readonly value: unknown;
  readonly source: ContractFieldSource;
  /**
   * Krok P8F5 bod 1: pamet prevzeti udaje ze zakazky — z ceho hodnota vznikla
   * a co uz clovek posoudil. Editor ji POSILA ZPET pri volbe „Převzít /
   * Ponechat"; pri beznem psani ji doplni server (`mergeFactMemory`).
   */
  // Bez `| undefined`: hodnoty smlouvy se kopiruji do tela PATCHe
  // (`copyOfValues`), jehoz typ je generovany z OpenAPI a pod
  // `exactOptionalPropertyTypes` vyslovne `undefined` neprijme.
  readonly fromFact?: ContractFieldFromFactDto;
}

export interface ContractFieldFromFactDto {
  // `string[]` (ne `readonly`): tytez hodnoty jdou zpet do tela PATCHe
  // generovaneho z OpenAPI, ktere readonly pole neprijme.
  readonly factKeys: string[];
  readonly takenValue?: unknown;
  readonly reviewedValue?: unknown;
  readonly choice?: 'prevzit' | 'ponechat';
}

/** Krok P8F5 body 2 a 3: rozdil mezi smlouvou a zakazkou u jednoho pole. */
export interface ContractFactChangeDto {
  readonly fieldKey: string;
  readonly kind: 'zmena-v-zakazce' | 'lisi-se-od-zakazky';
  readonly factValue: unknown;
  readonly contractValue: unknown;
  readonly takenValue?: unknown;
  /**
   * Krok P8F5 bod 3: podklad pro „Uložit i do zakázky" — klic faktu a hodnota
   * uz PREVEDENA serverem do tvaru faktu. Chybi = pole se do karty zapsat
   * neda (slozene pole) a tlacitko se nenabizi.
   */
  readonly saveToCase?: { readonly factKey: string; readonly value: unknown } | undefined;
}

export interface ContractDto {
  readonly id: string;
  readonly caseId: string | null;
  readonly propertyId: string | null;
  readonly contractType: ContractType;
  readonly title: string;
  readonly sourceKind: 'demo' | 'office';
  readonly sourceKey: string;
  readonly locale: string;
  readonly status: ContractStatusValue;
  readonly subjectType: SubjectTypeKey | null;
  readonly customSubject: SubjectFormsDto | null;
  readonly serviceStates: Readonly<Record<string, ServiceState>>;
  readonly fieldValues: Readonly<Record<string, ContractFieldValueDto>>;
  readonly manualOverrides: Readonly<Record<string, string>>;
  readonly articles: readonly ContractArticleDto[];
  /**
   * Krok UXD3 bod 3: „Vyplněné údaje tučně" u TETO smlouvy (vychozi hodnotu
   * dostala ze vzoru kancelare). Nepovinne, aby starsi odpoved serveru bez
   * pole editor neshodila — tehdy se chova jako pred UXD3 (vypnuto).
   */
  readonly boldFilledFields?: boolean;
  /** Krok UXD3 bod 4: vyjimky per pole z tlacitka „B" vlevo ve formulari. */
  readonly boldFieldOverrides?: Readonly<Record<string, boolean>>;
  readonly currentVersionNo: number | null;
  readonly createdAt: string;
  readonly updatedAt: string;
}

/** Krok SML2 bod 2: kdo chybejici pole doplni (server `fill-plan.ts`). */
export type FieldFiller = 'katastr' | 'klienti' | 'makler';

export type FillerCounts = Readonly<Record<FieldFiller, number>>;

export interface ContractFillPlanArticleDto {
  readonly id: string;
  /** Cislo clanku (`IV`); prazdne u zahlavi smlouvy. */
  readonly roman: string;
  readonly title: string;
  readonly missingKeys: readonly string[];
  readonly missing: number;
  readonly missingByFiller: FillerCounts;
  /** Nejvyznamnejsi zdroj chybejicich poli clanku (pocita server). */
  readonly filler: FieldFiller | null;
  readonly later: number;
  readonly done: boolean;
}

export interface ContractFillPlanDto {
  readonly total: number;
  readonly filled: number;
  readonly missing: number;
  readonly later: number;
  readonly missingByFiller: FillerCounts;
  readonly articles: readonly ContractFillPlanArticleDto[];
  /**
   * Krok APP3 bod 1 (nalez A31): seznamy, ze kterych vznikla cisla vys —
   * panel kontrol i veta u brany podpisu z nich jen CTOU, uz nepocitaji
   * vlastni mnozinu. `blockingKeys` je podmnozina `missingKeys` (to, na cem
   * stoji podpis; zbytek doplni strana sama na podpisove strance).
   */
  readonly missingKeys: readonly string[];
  readonly laterKeys: readonly string[];
  readonly blockingKeys: readonly string[];
  /** Chybejici pole, ktera vzor v textu nema (krok EDIT2 bod 3). */
  readonly missingOutsideText: readonly string[];
  readonly blocking: number;
  readonly cadastreKeys: readonly string[];
  /** PROP1: pole, ktera umi doplnit fakta zakazky (nabor, dotaznik majitele). */
  readonly caseFactKeys: readonly string[];
  /** Krok APP3 bod 2: pole, ktera umi doplnit nastaveni kancelare. */
  readonly officeKeys: readonly string[];
  /** Kdo doplni ktere pole — jedno misto, server (`fill-plan.ts`). */
  readonly fillerByKey: Readonly<Record<string, FieldFiller>>;
}

export type CadastreSkipReason = 'bez-faktu' | 'uz-vyplneno';

export interface ContractCadastrePrefillResultDto {
  readonly contract: ContractDetailDto;
  readonly filled: readonly string[];
  readonly skipped: readonly { readonly fieldKey: string; readonly reason: CadastreSkipReason }[];
}

export interface ContractDetailDto extends ContractDto {
  readonly renderedHtml: string;
  /**
   * Krok PRF5 bod 4: SAZBA dokumentu (`CONTRACT_DOC_CSS` v @reos/documents).
   * Admin si ji uz nedrzi sam — markup i stylopis posila ten, kdo dokument
   * sklada, takze editor, tisk a klientsky nahled maji tutez.
   */
  readonly documentCss: string;
  /**
   * Krok T4: stejny text jako `renderedHtml`, ale kazde dosazene pole je
   * obaleno do `<span class="reos-f reos-f--…" data-f>` podle puvodu udaje
   * (legenda klikaciho mockupu). Pro zivy nahled; tisk/PDF pouzivaji cisty
   * `renderedHtml`.
   */
  readonly renderedHtmlWithOrigins: string;
  /** Puvod kazdeho znameho pole — legenda v levem panelu editoru. */
  readonly fieldOrigins: Readonly<Record<string, FieldOrigin>>;
  readonly unfilled: readonly string[];
  readonly checks: readonly ContractCheckDto[];
  /**
   * Krok SML2 bod 1: `false` = smlouva jeste nejde odeslat. Rozhoduje SERVER
   * (`isComplete` nad temi kontrolami, ktere posila v `checks`) — admin si
   * pravidlo nekopiruje, jinak by tlacitko tvrdilo neco jineho nez brana.
   */
  readonly complete: boolean;
  /** Krok SML2 bod 2: osnova s rozpadem chybejicich poli podle zdroje. */
  readonly fillPlan: ContractFillPlanDto;
  /** Krok SML2 bod 5: pole povinna pro odeslani (seznam urcuje server). */
  readonly requiredKeys: readonly string[];
  /**
   * Krok SML3 bod 2: zmeny proti MUSTRU, ze ktereho smlouva vznikla — po
   * odstavcich, v poradi dokumentu. Pocita je server nad textem sablony,
   * takze zmena hodnoty pole zmenou textu NENI.
   */
  readonly templateChanges?: readonly ContractTextChangeDto[];
  /**
   * Krok SML3 bod 2: dokument se sazbou zmen (smazane preskrtnute, pridane
   * podtrzene) pro prepinac „Zobrazit změny". `null` = smlouva se od mustru
   * nelisi.
   */
  readonly renderedHtmlWithChanges?: string | null;
  /** Krok SML3 bod 4: `false` = text uz menit nelze (viz `textLockReason`). */
  readonly textEditable?: boolean;
  readonly textLockReason?: ContractTextLockReason | null;
  /**
   * Krok PIL3 bod 2 (nalez F03): PRIPRAVENOST K PODPISU. Rozhoduje SERVER
   * (`signingReadiness`) nad TYMIZ kontrolami a TYMZ textem, jaky uvidi
   * klient — admin si pravidlo nekopiruje. Nepovinne, aby starsi odpoved
   * serveru (bez pole) obrazovku neshodila; tehdy se jen nic nebrzdi.
   */
  readonly signing?: SigningReadinessDto;
}

/** Krok PIL3 bod 2: proc smlouva jeste nemuze k podpisu (kody ze serveru). */
export type SigningBlockerCode =
  | 'chybi-povinne'
  | 'kontrola-chyba'
  | 'zastupny-text'
  | 'nevyresena-alternativa';

export interface SigningBlockerDto {
  readonly code: SigningBlockerCode;
  readonly count: number;
  readonly sample: string | null;
  /**
   * Krok PRF1 (vady V1a, V5): popisky KONKRETNICH poli ze slovniku. Hlaska
   * z nich pise „Chybí: číslo LV, k. ú. …" misto „1 zástupné místo v textu",
   * ktere makleřovi v pilotu nerekla, co doplnit. Prazdne = prekazka z textu
   * mustru, kterou zadne pole nepopisuje.
   */
  readonly fields: readonly string[];
}

/**
 * Krok TOKYFIX1 (prekazka P6/2): nevyresena volba v textu smlouvy
 * („Kauce se platí … má/nemá", „do X dnů"). Co je volba a jak bude text po
 * rozhodnuti vypadat, rozhoduje SERVER (`unresolvedTextChoices`) — formular
 * si pravidlo nekopiruje, jen nabidne pole a ulozi `blockTextTemplate`
 * s dosazenou hodnotou.
 */
export interface UnresolvedTextChoiceDto {
  readonly blockId: string;
  readonly articleId: string;
  readonly articleTitle: string;
  readonly occurrence: number;
  /** Co v textu stoji — `má/nemá`, `X dnů`. */
  readonly raw: string;
  readonly kind: 'volba' | 'pocet' | 'text';
  readonly options: readonly string[];
  readonly unit: string;
  readonly blockText: string;
  readonly blockTextTemplate: string;
  readonly placeholder: string;
}

export interface SigningReadinessDto {
  readonly ready: boolean;
  readonly blockers: readonly SigningBlockerDto[];
  /**
   * Krok TOKYFIX1 (prekazka P6/2): volby v textu, ktere jde rozhodnout ve
   * formulari „Vyplnit". Nepovinne — starsi odpoved serveru pole nema.
   */
  readonly choices?: readonly UnresolvedTextChoiceDto[];
}

/** Krok SML3 bod 2: co se s odstavcem proti mustru stalo. */
export type TextChangeKind = 'upraveno' | 'vlozeno' | 'smazano';
export type ContractTextLockReason = 'podepsano' | 'odeslano-k-podpisu';

export interface ContractTextChangeDto {
  readonly blockId: string;
  readonly articleId: string;
  readonly articleRoman: string;
  readonly articleTitle: string;
  readonly paragraphNo: number;
  /** Adresa odstavce v dokumentu — „VI/2". */
  readonly position: string;
  readonly kind: TextChangeKind;
  readonly templateText: string;
  readonly currentText: string;
  readonly at: string | null;
  readonly by: string | null;
  readonly clauseTitle: string | null;
}

/** Krok SML3 bod 3: klauzule knihovny kancelare. */
export interface ContractClauseDto {
  readonly id: string;
  /** `reos` = standardni klauzule REOS (GDPR1), jen pro cteni. */
  readonly source: 'kancelar' | 'reos';
  readonly title: string;
  readonly note: string | null;
  readonly body: string;
  readonly createdAt: string;
  readonly updatedAt: string;
}

export interface ContractVersionDto {
  readonly id: string;
  readonly contractId: string;
  readonly versionNo: number;
  readonly renderedHtml: string;
  readonly changeNote: string | null;
  readonly createdBy: string | null;
  readonly createdAt: string;
}

export interface ContractPrintResultDto {
  readonly html: string;
  /** Krok PRF5 bod 4: sazba dokumentu k tomuhle HTML. */
  readonly documentCss: string;
  readonly complete: boolean;
}

// -------------------------------------------------------------------- katalogy

export async function listDemoTemplates(
  skupina?: 'A' | 'B' | 'C' | 'D',
): Promise<DemoTemplateSummaryDto[]> {
  return (await unwrap(
    apiClient.GET('/v1/contract-templates/demo', {
      params: { query: skupina === undefined ? {} : { skupina } },
    }),
  )) as DemoTemplateSummaryDto[];
}

/**
 * Krok NOC4 bod 6 (nalez N25): katalog sluzeb patri TYPU smlouvy — najem
 * prostoru slouziciho podnikani nema bytovy vycet z. 67/2013. Rozhoduje
 * SERVER (`serviceCatalogFor`), admin se jen zepta za svou smlouvu; bez typu
 * prijde bytovy katalog (obrazovka „Nová smlouva", kde typ jeste nikdo nezvolil).
 *
 * `as never` je tu proto, ze typy klienta vznikaji z `docs/api/openapi.json`
 * a ten je v repu ZASTARALY vuci kodu (regenerace pritahne 240 radku cizich
 * zmen z jinych kroku — zapsano v „Nalezy" reportu NOC4). Parametr je
 * NEPOVINNY, takze starsi server i starsi vygenerovane typy zustavaji platne.
 */
export async function getContractCatalogs(contractType?: string): Promise<ContractCatalogsDto> {
  return (await unwrap(
    apiClient.GET('/v1/contract-templates/catalogs', {
      ...(contractType === undefined ? {} : { params: { query: { contractType } } }),
    } as never),
  )) as ContractCatalogsDto;
}

// ------------------------------------------ podklad „Nova smlouva" (krok SML1)

export type SignatureLevelValue = 'prosty' | 'zaruceny' | 'kvalifikovany';
export type SignatureRouteValue =
  'magic-link' | 'signi' | 'bankid-sign' | 'bankid-qsign' | 'papir-overeny';

/**
 * Rozhodnuti politiky podpisu. Shodny tvar jako `SignaturePolicyDto`
 * v `signature-api.ts` (tentyz serverovy schema) — obrazovka „Nová smlouva"
 * ho potrebuje JESTE NEZ smlouva vznikne, takze chodi v tomhle podkladu.
 */
export interface ContractSignaturePolicyDto {
  readonly level: SignatureLevelValue;
  readonly levelLabelKey: string;
  readonly route: SignatureRouteValue;
  readonly routeLabelKey: string;
  readonly reasonKey: string;
  readonly source: 'zakladni' | 'vzor' | 'kancelar';
  readonly floorApplied: boolean;
  readonly requiresBankIdentity: boolean;
  readonly electronic: boolean;
  readonly cadastralCapable: boolean;
  readonly blocking: readonly string[];
  readonly warnings: readonly string[];
}

export interface NewContractTypePlanDto {
  readonly contractType: ContractType;
  readonly policy: ContractSignaturePolicyDto;
}

export interface NewContractTemplatePlanDto {
  readonly kind: 'office' | 'demo';
  readonly key: string;
  readonly name: string;
  readonly contractType: ContractType;
  readonly version: number | null;
  readonly total: number;
  readonly missingByFiller: FillerCounts;
  readonly later: number;
  readonly labelsByFiller: Readonly<Record<FieldFiller, readonly string[]>>;
  readonly policy: ContractSignaturePolicyDto;
}

export interface NewContractPlanDto {
  readonly types: readonly NewContractTypePlanDto[];
  readonly templates: readonly NewContractTemplatePlanDto[];
}

/**
 * Krok SML1 bod 2: uroven podpisu kazdeho typu (`levels.ts`) a rozpad
 * „kdo doplni" kazde sablony (`fill-plan.ts`) — jedno volani, zadny odhad v UI.
 */
export async function getNewContractPlan(): Promise<NewContractPlanDto> {
  return (await unwrap(
    apiClient.GET('/v1/contract-templates/new-plan', {}),
  )) as NewContractPlanDto;
}

// ---------------------------------------------------------------- office sablony

export async function listOfficeTemplates(
  status?: OfficeTemplateStatus,
): Promise<OfficeTemplateDto[]> {
  return (await unwrap(
    apiClient.GET('/v1/contract-templates', {
      params: { query: status === undefined ? {} : { status } },
    }),
  )) as OfficeTemplateDto[];
}

export async function getOfficeTemplate(id: string): Promise<OfficeTemplateDto> {
  return (await unwrap(
    apiClient.GET('/v1/contract-templates/{id}', { params: { path: { id } } }),
  )) as OfficeTemplateDto;
}

type CreateOfficeTemplateBody =
  ApiPaths['/v1/contract-templates']['post']['requestBody']['content']['application/json'];

export async function createOfficeTemplate(
  body: CreateOfficeTemplateBody,
): Promise<OfficeTemplateDto> {
  return (await unwrap(apiClient.POST('/v1/contract-templates', { body }))) as OfficeTemplateDto;
}

/**
 * Krok UXD3 bod 2: prepnuti volby „Vyplněné údaje tučně" na vzoru kancelare.
 * Plati pro smlouvy, ktere ze vzoru TEPRVE vzniknou — uz rozdelane smlouvy si
 * volbu drzi samy (server, ROZHODNUTI UXD3-A).
 */
export async function setOfficeTemplateBoldFilledFields(
  id: string,
  boldFilledFields: boolean,
): Promise<OfficeTemplateDto> {
  return (await unwrap(
    apiClient.POST('/v1/contract-templates/{id}/bold-filled-fields', {
      params: { path: { id } },
      body: { boldFilledFields },
    }),
  )) as OfficeTemplateDto;
}

export async function createOfficeTemplateFromDemo(
  demoId: string,
  name?: string,
): Promise<OfficeTemplateDto> {
  return (await unwrap(
    apiClient.POST('/v1/contract-templates/from-demo/{demoId}', {
      params: { path: { demoId } },
      body: name === undefined ? {} : { name },
    }),
  )) as OfficeTemplateDto;
}

type ImportSuggestBody =
  ApiPaths['/v1/contract-templates/import/suggest']['post']['requestBody']['content']['application/json'];

/** Pracovni stav mapovaci obrazovky (klient dal upravuje/posila zpet) - MUTABLE pole, ne DTO k pouhemu cteni. */
export interface ImportSuggestResultDto {
  articles: ContractArticleDto[];
  suggestions: MappingSuggestionDto[];
  inflectionHits: InflectionHitDto[];
}

export async function suggestImportMapping(
  body: ImportSuggestBody,
): Promise<ImportSuggestResultDto> {
  return (await unwrap(
    apiClient.POST('/v1/contract-templates/import/suggest', { body }),
  )) as ImportSuggestResultDto;
}

/** Krok T5 bod 20: totez, ale ze skutecne nahraneho `.docx`. */
export interface DocxImportResultDto extends ImportSuggestResultDto {
  highlights: string[];
  paragraphCount: number;
  storageKey: string;
  /** Krok SML6 bod 4: titul smlouvy z dokumentu = nazev sablony. */
  documentTitle?: string | null;
  /** Krok SML6 bod 2: typ smlouvy rozpoznany z dokumentu (`null` = nepoznano). */
  detectedContractType?: ContractType | null;
}

/**
 * Nahrani `.docx` NEJDE pres `apiClient`: je to multipart s binarnim telem,
 * takze se pouziva `fetch` se stejnym base URL a cookies — stejny vzor jako
 * `uploadHumanTaskDocument` v `human-tasks-api.ts`.
 */
export async function importContractTemplateDocx(file: File): Promise<DocxImportResultDto> {
  const form = new FormData();
  form.append('file', file, file.name);
  const response = await fetch(`${baseUrl}/v1/contract-templates/import/docx`, {
    method: 'POST',
    credentials: 'include',
    body: form,
  });
  if (!response.ok) {
    // Telo je problem+json; detail se vypisuje uzivateli, proto se nezahazuje.
    const problem = (await response.json().catch(() => null)) as
      | { detail?: string; title?: string }
      | null;
    throw new Error(problem?.detail ?? problem?.title ?? `HTTP ${response.status}`);
  }
  return (await response.json()) as DocxImportResultDto;
}

type ImportApplyBody =
  ApiPaths['/v1/contract-templates/import/apply']['post']['requestBody']['content']['application/json'];
export type ImportApplyDecision = ImportApplyBody['decisions'][number];

export async function applyImportMapping(
  body: ImportApplyBody,
): Promise<{ articles: ContractArticleDto[]; skipped?: MappingSkipDto[] }> {
  return (await unwrap(apiClient.POST('/v1/contract-templates/import/apply', { body }))) as {
    articles: ContractArticleDto[];
    skipped?: MappingSkipDto[];
  };
}

/**
 * Krok APP3 bod 4 (nalez A40) — ADRESA ZE SMLOUVY DO ADRESARE (a tim na
 * fakturu). `confirm: true` = vyslovne potvrzeni prepisu; bez nej server
 * jinou adresu NEPREPISE a vrati ji v `conflicts`.
 */
export interface ContractPartyAddressSyncDto {
  readonly written: readonly {
    readonly role: string;
    readonly partyKey: 'prodavajici' | 'kupujici' | 'pronajimatel' | 'najemce';
    readonly index: number;
    readonly address: string;
  }[];
  readonly conflicts: readonly {
    readonly role: string;
    readonly partyKey: 'prodavajici' | 'kupujici' | 'pronajimatel' | 'najemce';
    readonly index: number;
    readonly address: string;
    readonly existing: string;
  }[];
  readonly skipped: readonly {
    readonly role: string;
    readonly partyKey: 'prodavajici' | 'kupujici' | 'pronajimatel' | 'najemce';
    readonly index: number;
    readonly reason: 'shodna-adresa' | 'bez-osoby';
  }[];
}

export async function syncContractPartyAddresses(
  id: string,
  confirm: boolean,
): Promise<ContractPartyAddressSyncDto> {
  return (await unwrap(
    apiClient.POST('/v1/contracts/{id}/party-address-sync', {
      params: { path: { id } },
      body: { confirm },
    }),
  )) as ContractPartyAddressSyncDto;
}

export async function listCustomSubjects(): Promise<CustomSubjectDto[]> {
  return (await unwrap(
    apiClient.GET('/v1/contract-templates/custom-subjects', {}),
  )) as CustomSubjectDto[];
}

type CreateCustomSubjectBody =
  ApiPaths['/v1/contract-templates/custom-subjects']['post']['requestBody']['content']['application/json'];

export async function createCustomSubject(
  body: CreateCustomSubjectBody,
): Promise<CustomSubjectDto> {
  return (await unwrap(
    apiClient.POST('/v1/contract-templates/custom-subjects', { body }),
  )) as CustomSubjectDto;
}

// -------------------------------------------------------------------- smlouvy

export interface ListContractsFilter {
  readonly status?: ContractStatusValue;
  readonly contractType?: ContractType;
  readonly caseId?: string;
}

export async function listContracts(filter: ListContractsFilter = {}): Promise<ContractDto[]> {
  return (await unwrap(
    apiClient.GET('/v1/contracts', { params: { query: filter } }),
  )) as ContractDto[];
}

export async function getContract(id: string): Promise<ContractDetailDto> {
  return (await unwrap(
    apiClient.GET('/v1/contracts/{id}', { params: { path: { id } } }),
  )) as ContractDetailDto;
}

/**
 * Krok P8F5 body 2–4: co se v zakazce zmenilo od prevzeti udaje do smlouvy.
 * Porovnani dela SERVER (fakta zakazky zna jen on a mapa pole→fakt je jeho) —
 * admin by druhou kopii pravidla jen rozesel s tim, co „Doplnit ze zakázky"
 * skutecne dosadi. Mimo stav `draft` vraci server prazdny seznam.
 */
export async function getContractFactChanges(
  id: string,
): Promise<readonly ContractFactChangeDto[]> {
  return (await unwrap(
    apiClient.GET('/v1/contracts/{id}/fact-changes', { params: { path: { id } } }),
  )) as readonly ContractFactChangeDto[];
}

type CreateContractBody =
  ApiPaths['/v1/contracts']['post']['requestBody']['content']['application/json'];

export async function createContract(body: CreateContractBody): Promise<ContractDetailDto> {
  return (await unwrap(apiClient.POST('/v1/contracts', { body }))) as ContractDetailDto;
}

type UpdateContractBody =
  ApiPaths['/v1/contracts/{id}']['patch']['requestBody']['content']['application/json'];

export async function updateContract(
  id: string,
  body: UpdateContractBody,
): Promise<ContractDetailDto> {
  return (await unwrap(
    apiClient.PATCH('/v1/contracts/{id}', { params: { path: { id } }, body }),
  )) as ContractDetailDto;
}

/**
 * Krok SML2 bod 3: „Doplnit N údajů z katastru". Hodnoty hleda SERVER
 * (fakta zakazky zna jen on) a vraci vedle smlouvy i to, co doplnit NEMOHL
 * a proc — editor to vypise, aby tlacitko nikdy nic nepredstiralo.
 */
export async function prefillContractFromCadastre(
  id: string,
): Promise<ContractCadastrePrefillResultDto> {
  return (await unwrap(
    apiClient.POST('/v1/contracts/{id}/prefill-cadastre', {
      params: { path: { id } },
      body: {},
    }),
  )) as ContractCadastrePrefillResultDto;
}

export async function deleteContract(id: string): Promise<void> {
  await unwrap(apiClient.DELETE('/v1/contracts/{id}', { params: { path: { id } } }));
}

export async function setBlockOverride(
  contractId: string,
  blockId: string,
  text: string,
): Promise<ContractDetailDto> {
  return (await unwrap(
    apiClient.PUT('/v1/contracts/{id}/blocks/{blockId}/override', {
      params: { path: { id: contractId, blockId } },
      body: { text },
    }),
  )) as ContractDetailDto;
}

export async function revertBlockOverride(
  contractId: string,
  blockId: string,
): Promise<ContractDetailDto> {
  return (await unwrap(
    apiClient.DELETE('/v1/contracts/{id}/blocks/{blockId}/override', {
      params: { path: { id: contractId, blockId } },
    }),
  )) as ContractDetailDto;
}

/**
 * „Vrátit na mustr" (krok SML3 bod 2) — JEDNA cesta pro prepsany, vlozeny
 * i smazany odstavec. Druh zmeny urci server z diffu; admin ho nehada.
 */
export async function revertBlockToTemplate(
  contractId: string,
  blockId: string,
): Promise<ContractDetailDto> {
  return (await unwrap(
    apiClient.POST('/v1/contracts/{id}/blocks/{blockId}/revert-to-template', {
      params: { path: { id: contractId, blockId } },
      body: {},
    }),
  )) as ContractDetailDto;
}

/** Odebrani odstavce z textu TETO smlouvy (mustr zustava beze zmeny). */
export async function removeContractBlock(
  contractId: string,
  blockId: string,
): Promise<ContractDetailDto> {
  return (await unwrap(
    apiClient.DELETE('/v1/contracts/{id}/blocks/{blockId}', {
      params: { path: { id: contractId, blockId } },
    }),
  )) as ContractDetailDto;
}

/** „+ Vložit klauzuli" — nový odstavec článku na místo kurzoru (krok SML3 bod 3). */
export async function insertContractClause(
  contractId: string,
  input: { readonly clauseId: string; readonly articleId: string; readonly afterBlockId?: string },
): Promise<ContractDetailDto> {
  return (await unwrap(
    apiClient.POST('/v1/contracts/{id}/clauses', {
      params: { path: { id: contractId } },
      body: {
        clauseId: input.clauseId,
        articleId: input.articleId,
        ...(input.afterBlockId === undefined ? {} : { afterBlockId: input.afterBlockId }),
      },
    }),
  )) as ContractDetailDto;
}

/** „Uložit jako nový mustr" — nová verze šablony kanceláře (krok SML3 bod 1). */
export async function saveContractAsTemplate(
  contractId: string,
  name?: string,
): Promise<OfficeTemplateDto> {
  return (await unwrap(
    apiClient.POST('/v1/contracts/{id}/save-as-template', {
      params: { path: { id: contractId } },
      body: name === undefined ? {} : { name },
    }),
  )) as OfficeTemplateDto;
}

// ------------------------------------- knihovna klauzuli kancelare (krok SML3)

export async function listContractClauses(q?: string): Promise<ContractClauseDto[]> {
  const result = (await unwrap(
    apiClient.GET('/v1/contract-clauses', {
      params: { query: q === undefined || q === '' ? {} : { q } },
    }),
  )) as { items: ContractClauseDto[] };
  return result.items;
}

export async function createContractClause(input: {
  readonly title: string;
  readonly note?: string;
  readonly body: string;
}): Promise<ContractClauseDto> {
  return (await unwrap(
    apiClient.POST('/v1/contract-clauses', {
      body: {
        title: input.title,
        body: input.body,
        ...(input.note === undefined || input.note === '' ? {} : { note: input.note }),
      },
    }),
  )) as ContractClauseDto;
}

export async function updateContractClause(
  id: string,
  input: { readonly title?: string; readonly note?: string; readonly body?: string },
): Promise<ContractClauseDto> {
  return (await unwrap(
    apiClient.PATCH('/v1/contract-clauses/{id}', { params: { path: { id } }, body: input }),
  )) as ContractClauseDto;
}

export async function archiveContractClause(id: string): Promise<void> {
  await unwrap(apiClient.DELETE('/v1/contract-clauses/{id}', { params: { path: { id } } }));
}

export async function createContractVersion(
  id: string,
  changeNote?: string,
): Promise<ContractVersionDto> {
  return (await unwrap(
    apiClient.POST('/v1/contracts/{id}/versions', {
      params: { path: { id } },
      body: changeNote === undefined ? {} : { changeNote },
    }),
  )) as ContractVersionDto;
}

export async function listContractVersions(id: string): Promise<ContractVersionDto[]> {
  return (await unwrap(
    apiClient.GET('/v1/contracts/{id}/versions', { params: { path: { id } } }),
  )) as ContractVersionDto[];
}

export async function transitionContractStatus(
  id: string,
  status: ContractStatusValue,
): Promise<ContractDetailDto> {
  return (await unwrap(
    apiClient.POST('/v1/contracts/{id}/status', { params: { path: { id } }, body: { status } }),
  )) as ContractDetailDto;
}

export async function printContract(id: string): Promise<ContractPrintResultDto> {
  return (await unwrap(
    apiClient.GET('/v1/contracts/{id}/print', { params: { path: { id } } }),
  )) as ContractPrintResultDto;
}

/**
 * Zivy pruchod 13. 10. (V3): „Stáhnout PDF" musi dat SKUTECNY soubor ze
 * serveru. Telo (binarni odpoved mimo `apiClient` + opakovani pri pretrzenem
 * spojeni) zije v `downloadServerPdf` v `api.ts` — tutez cestu ma
 * `downloadInvoicePdf` v `invoices-api.ts` (krok QAFIX7).
 */
export async function downloadContractPdf(id: string, filename: string): Promise<void> {
  await downloadServerPdf(`${baseUrl}/v1/contracts/${id}/print.pdf`, filename);
}

// ------------------------------------------------ lhuty ze smluv (krok T5 bod 6)

export type ContractDeadlineKind = 'predani' | 'kauce' | 'prvni-platba' | 'konec-najmu';

export interface ContractDeadlineDto {
  readonly id: string;
  readonly contractId: string;
  readonly caseId: string | null;
  readonly kind: ContractDeadlineKind;
  readonly sourceField: string;
  readonly title: string;
  readonly dueDate: string;
  readonly status: 'open' | 'done';
  readonly completedAt: string | null;
}

/** Zalozi (nebo prepocita) ukoly k lhutam smlouvy. */
export async function syncContractDeadlines(id: string): Promise<ContractDeadlineDto[]> {
  const result = (await unwrap(
    apiClient.POST('/v1/contracts/{id}/deadlines', { params: { path: { id } }, body: {} }),
  )) as { items: ContractDeadlineDto[] };
  return result.items;
}

export async function listContractDeadlines(id: string): Promise<ContractDeadlineDto[]> {
  const result = (await unwrap(
    apiClient.GET('/v1/contracts/{id}/deadlines', { params: { path: { id } } }),
  )) as { items: ContractDeadlineDto[] };
  return result.items;
}

/**
 * Lhuty ze VSECH smluv (krok SML1 bod 1 — panel „Lhůty ze smluv" v prehledu).
 * Tentyz endpoint, ktery uz zna zakazka (`?caseId=`), jen bez filtru; lhuty
 * zaklada a pocita krok T5, tady se jen vypisuji.
 */
export async function listAllContractDeadlines(
  caseId?: string | undefined,
): Promise<ContractDeadlineDto[]> {
  const result = (await unwrap(
    apiClient.GET('/v1/contract-deadlines', {
      params: { query: caseId === undefined ? {} : { caseId } },
    }),
  )) as { items: ContractDeadlineDto[] };
  return result.items;
}

export async function setContractDeadlineStatus(
  id: string,
  status: 'open' | 'done',
): Promise<ContractDeadlineDto> {
  return (await unwrap(
    apiClient.POST('/v1/contract-deadlines/{id}/status', {
      params: { path: { id } },
      body: { status },
    }),
  )) as ContractDeadlineDto;
}

// ------------------------------------- AI akce nad smlouvou (krok NAP7 A17)

/**
 * Tri akce, jejichz jadro v `@reos/ai` existovalo od kroku AI7 a do NAP7
 * nemelo volajiciho. `aiAvailable: false` NENI chyba — AI muze byt pro
 * kancelar vypnuta nebo bez kreditu; obrazovka na to ma hlasku.
 */
export interface ContractAiContradictionDto {
  readonly factKeyA: string;
  readonly factKeyB: string;
  readonly explanation: string;
}

export interface ContractAiReviewResultDto {
  readonly aiAvailable: boolean;
  /** Deterministicke rozpory proti reportu a formulari (server, `crossSourceChecks`). */
  readonly checks: readonly ContractCheckDto[];
  readonly contradictions: readonly ContractAiContradictionDto[];
  /** `true` = zakazka nema v REOS ani report, ani klientsky formular. */
  readonly noSources: boolean;
}

export interface ContractAiExplanationResultDto {
  readonly aiAvailable: boolean;
  readonly explanation: string | null;
  readonly disclaimer: string | null;
}

export interface ContractValueChangeDto {
  readonly key: string;
  readonly ours: string | null;
  readonly theirs: string | null;
  /** Krok AI7 Hulan c: cena je nejdulezitejsi zmena — UI ji zvyrazni. */
  readonly isPriceField: boolean;
}

export interface ContractVersionDiffResultDto {
  readonly versionNo: number;
  readonly previousVersionNo: number | null;
  readonly changes: readonly ContractValueChangeDto[];
  readonly available: boolean;
}

export async function reviewContractAgainstSources(
  id: string,
): Promise<ContractAiReviewResultDto> {
  return (await unwrap(
    apiClient.POST('/v1/contracts/{id}/ai/review', { params: { path: { id } }, body: {} }),
  )) as ContractAiReviewResultDto;
}

export async function explainContractToClient(
  id: string,
): Promise<ContractAiExplanationResultDto> {
  return (await unwrap(
    apiClient.POST('/v1/contracts/{id}/ai/explain', { params: { path: { id } }, body: {} }),
  )) as ContractAiExplanationResultDto;
}

export async function diffContractVersion(
  id: string,
  versionNo: number,
): Promise<ContractVersionDiffResultDto> {
  return (await unwrap(
    apiClient.GET('/v1/contracts/{id}/versions/{versionNo}/diff', {
      params: { path: { id, versionNo } },
    }),
  )) as ContractVersionDiffResultDto;
}
